"use client";

import { useState, useEffect, useRef } from "react";

export function useThrottle<T>(value: T, delay: number): T {
	const [throttledValue, setThrottledValue] = useState<T>(value);
	const lastExecuted = useRef<number>(0);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		const now = Date.now();
		const timeSinceLastExecution = now - lastExecuted.current;

		const update = () => {
			setThrottledValue(value);
			lastExecuted.current = Date.now();
		};

		if (timeSinceLastExecution >= delay) {
			// If enough time has passed, update immediately
			update();
		} else {
			// Otherwise, schedule an update for the remaining time
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}

			timeoutRef.current = setTimeout(update, delay - timeSinceLastExecution);
		}

		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, [value, delay]);

	return throttledValue;
}

export default useThrottle;
